import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';
import StatusInfo from '../components/StatusInfo';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 30;

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDateOnly(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const TARGET_STATUS_LABEL = { active: 'Активно', failed: 'Ошибка', untested: 'Не проверено' };
const TARGET_STATUS_COLOR = { active: '#2EC4B6', failed: '#f87171', untested: '#64748b' };

export default function PollLog() {
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState([]);
  const [targets, setTargets] = useState([]);
  const [showAllTargets, setShowAllTargets] = useState(false);

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const loadRuns = useCallback(() => {
    axios.get(`${API}/admin/poll-runs`, { headers, params: { limit: LIMIT, offset } })
      .then(r => { setRuns(r.data.items); setRunsTotal(r.data.total); });
    // eslint-disable-next-line
  }, [offset]);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    axios.get(`${API}/admin/poll-runs/targets/status`, { headers }).then(r => setTargets(r.data.items));
    // eslint-disable-next-line
  }, []);

  const toggleExpand = (run) => {
    if (expanded === run.id) { setExpanded(null); return; }
    setExpanded(run.id);
    axios.get(`${API}/admin/poll-runs/${run.id}`, { headers }).then(r => setExpandedErrors(r.data.errors || []));
  };

  const problemTargets = targets.filter(t => t.poll_error_count > 0 || t.api_status === 'failed');
  const shownTargets = showAllTargets ? targets : problemTargets;

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>API</h1>
      </div>
      <div style={styles.hint}>
        Итоги ежедневного опроса клиник/сетей (daily_poll) — заходите сюда с утра, чтобы
        проверить, всё ли прошло штатно ночью и сколько новых данных получено.
      </div>

      {/* ── Текущий статус целей ── */}
      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>
          Текущий статус целей
          <StatusInfo def="poll_target_status" />
        </h2>
        {problemTargets.length === 0 ? (
          <span style={styles.okBadge}>Проблем не найдено</span>
        ) : (
          <span style={styles.errBadge}>{problemTargets.length} с ошибками</span>
        )}
        {targets.length > problemTargets.length && (
          <span style={styles.toggleAll} onClick={() => setShowAllTargets(v => !v)}>
            {showAllTargets ? 'Показать только проблемные' : `Показать все (${targets.length})`}
          </span>
        )}
      </div>
      <div style={styles.table}>
        <div style={{ ...styles.thead, gridTemplateColumns: targetCols }}>
          <div>Цель<StatusInfo text="Название клиники или сети." /></div>
          <div>Тип<StatusInfo text="Клиника или сеть." /></div>
          <div>Статус<StatusInfo text="Реальный статус подключения API — обновляется ночным опросом, не только ручным пингом из админки." /></div>
          <div>Ошибок подряд<StatusInfo text="Сколько раз подряд не удалось опросить эту цель — обнуляется первым же успешным опросом." /></div>
          <div>Последняя ошибка<StatusInfo text="Текст и время последней ошибки опроса этой цели." /></div>
        </div>
        {shownTargets.length === 0 ? (
          <div style={styles.empty}>{problemTargets.length === 0 ? 'Все цели опрашиваются без ошибок' : 'Ничего не найдено'}</div>
        ) : shownTargets.map(t => (
          <div key={`${t.target_type}:${t.target_id}`} style={{ ...styles.trow, gridTemplateColumns: targetCols }}>
            <div style={styles.strong}>{t.name}</div>
            <div style={styles.faint}>{t.target_type === 'network' ? 'сеть' : 'клиника'}</div>
            <div><span style={{ ...styles.badge, color: TARGET_STATUS_COLOR[t.api_status], borderColor: TARGET_STATUS_COLOR[t.api_status] }}>{TARGET_STATUS_LABEL[t.api_status] || t.api_status}</span></div>
            <div style={t.poll_error_count > 0 ? styles.errText : styles.faint}>{t.poll_error_count}</div>
            <div style={styles.faint}>{t.last_poll_error ? `${t.last_poll_error} (${fmtDate(t.last_poll_error_at)})` : '—'}</div>
          </div>
        ))}
      </div>

      {/* ── История опросов по дням ── */}
      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>
          Опрос по дням
          <StatusInfo def="poll_run" />
        </h2>
      </div>
      <div style={styles.table}>
        <div style={{ ...styles.thead, gridTemplateColumns: runCols }}>
          <div>Дата<StatusInfo text="Календарный день, за который прошёл опрос — не дата запуска." /></div>
          <div>Обновлено<StatusInfo text="Когда этот день последний раз переопрашивался (переопрашивается ещё несколько следующих ночей, скользящее окно catch-up)." /></div>
          <div>Проверено<StatusInfo text="Размер пула активных направлений на момент опроса — не «сколько создано за день». Может стоять на месте, даже если активность идёт: см. столбец «Новых направлений»." /></div>
          <div>Новых направлений<StatusInfo text="Сколько направлений реально привязано врачами именно в этот день." /></div>
          <div>Подтверждено<StatusInfo text="Прошли проверку на дубль в этот день: Направлен → Подтверждён." /></div>
          <div>Дубли<StatusInfo text="Признаны дублем в этот день — пациент уже был клиентом клиники до привязки." /></div>
          <div>Не дошли<StatusInfo text="Сгорели по таймеру в этот день — пациент не оплатил ни разу." /></div>
          <div>Операции<StatusInfo text="Направления «Операции», закрытые в этот день — истекло окно." /></div>
          <div>Оплаты<StatusInfo text="Найдено отдельных оплат по всем направлениям в этот день." /></div>
          <div>Ошибки<StatusInfo text="Сколько раз не удалось получить ответ от API хотя бы одного источника — направления просто переопрашиваются позже, ничего не теряется." /></div>
        </div>
        {runs.length === 0 ? (
          <div style={styles.empty}>Опросов пока не было</div>
        ) : runs.map(r => (
          <div key={r.id}>
            <div style={{ ...styles.trow, gridTemplateColumns: runCols, cursor: r.error_count > 0 ? 'pointer' : 'default' }}
                 onClick={() => r.error_count > 0 && toggleExpand(r)}>
              <div style={styles.strong}>{fmtDateOnly(r.poll_date)}</div>
              <div style={styles.faint}>{fmtDate(r.run_at)}</div>
              <div>{r.referrals_checked}</div>
              <div>{r.new_referrals}</div>
              <div>{r.confirmed}</div>
              <div>{r.duplicates}</div>
              <div>{r.timed_out}</div>
              <div>{r.operations_closed}</div>
              <div style={styles.money}>{r.payments}</div>
              <div>
                {r.error_count > 0
                  ? <span style={styles.errBadgeSm}>{r.error_count} {expanded === r.id ? '▲' : '▼'}</span>
                  : <span style={styles.okBadgeSm}>OK</span>}
              </div>
            </div>
            {expanded === r.id && (
              <div style={styles.errorsPanel}>
                {expandedErrors.length === 0 ? (
                  <div style={styles.faint}>Загрузка…</div>
                ) : expandedErrors.map((e, i) => (
                  <div key={i} style={styles.errorRow}>
                    <span style={styles.faint}>{e.target_type === 'network' ? 'сеть' : 'клиника'}</span> {e.target_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <ListPager total={runsTotal} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const targetCols = '1.6fr 0.7fr 0.9fr 1fr 2fr';
const runCols = '0.9fr 1.2fr 0.9fr 1.1fr 0.9fr 0.7fr 0.8fr 0.8fr 0.8fr 0.9fr';

const styles = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  hint: { fontSize: 12, color: '#64748b', background: '#1e293b', borderRadius: 8, padding: '10px 16px', marginBottom: 20, borderLeft: '3px solid #334155' },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 10 },
  sectionTitle: { color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center' },
  okBadge: { fontSize: 12, fontWeight: 700, color: '#2EC4B6', background: 'rgba(46,196,182,0.12)', borderRadius: 20, padding: '3px 12px' },
  errBadge: { fontSize: 12, fontWeight: 700, color: '#f87171', background: 'rgba(248,113,113,0.12)', borderRadius: 20, padding: '3px 12px' },
  toggleAll: { fontSize: 12, color: '#2EC4B6', cursor: 'pointer', marginLeft: 'auto' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden', marginBottom: 28 },
  thead: { display: 'grid', padding: '10px 16px', borderBottom: '1px solid #334155', fontSize: 11, color: '#64748b', textTransform: 'uppercase' },
  trow: { display: 'grid', padding: '12px 16px', borderBottom: '1px solid #263548', fontSize: 13, color: '#e2e8f0', alignItems: 'center' },
  strong: { fontWeight: 600 },
  faint: { color: '#64748b', fontSize: 12 },
  money: { color: '#2EC4B6', fontWeight: 700 },
  errText: { color: '#f87171', fontWeight: 700 },
  badge: { border: '1px solid', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 },
  errBadgeSm: { color: '#f87171', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  okBadgeSm: { color: '#2EC4B6', fontWeight: 700, fontSize: 12 },
  empty: { color: '#64748b', fontSize: 13, padding: '20px 16px' },
  errorsPanel: { background: '#0f172a', padding: '12px 16px 12px 32px', borderBottom: '1px solid #263548' },
  errorRow: { fontSize: 12.5, color: '#e2e8f0', padding: '4px 0' },
};
